//@flow
import * as Immutable from 'immutable';
import {
    validate,
    validatable,
    softValidateAll,
    validateAll
} from 'common/utils/ValidationUtil';
import NumberUtils from 'common/utils/NumberUtils';
import I18nHelper from 'common/helpers/I18nHelper';
import DataUtils from 'common/utils/DataUtils';
import { Units } from 'common/enums/UnitEnum';

import { Dimensions } from 'common/models/flow/Dimensions';
import type { DimensionsModelType } from 'common/models/flow/Dimensions';

export const preferredUnitsForDistance = [
    Units.list.length.Meter,
    Units.list.length.Inch
];
export const sourceUnitForDistance = Units.list.length.Meter;
export const preferredUnitsForWeight = [
    Units.list.mass.Gram,
    Units.list.mass.Pound
];
export const sourceUnitForWeight = Units.list.mass.Gram;

const translationsNamespace = ['validations', 'entityManagement'];

const nsTranslate = I18nHelper.getTranslate(translationsNamespace);

@validatable
class InternalDimensions extends Dimensions {
    static fromJS(json: DimensionsModelType): InternalDimensions {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }

    @validate
    setLength(length: string): this {
        return super.setLength(length);
    }

    @validate
    setBreadth(bredth: string): this {
        return super.setBreadth(bredth);
    }

    @validate
    setHeight(height: string): this {
        return super.setHeight(height);
    }

    @softValidateAll
    hasValidationErrors(): boolean {
        return false;
    }

    @validateAll
    setAllValidationErrors(): this {
        const self = this;
        return self;
    }

    isEmpty() {
        if (!this.length && !this.breadth && !this.height) {
            return true;
        }
        return false;
    }

    isFilled() {
        if (this.length && this.breadth && this.height) {
            return true;
        }
        return false;
    }

    validateInputValue(value: string, dimensionType: string) {
        if (DataUtils.isEmpty(value)) {
            return '';
        }
        if (!NumberUtils.isNumeric(value)) {
            return nsTranslate('skuValidationErrors.dimensionNotANumber', {
                dimensionType
            });
        }
        if (NumberUtils.toNumber(value) === 0) {
            return nsTranslate('skuValidationErrors.nonZeroDimension', {
                dimensionType
            });
        }
        if (!NumberUtils.isPositiveNumber(value)) {
            return nsTranslate('skuValidationErrors.nonNumericDimension', {
                dimensionType
            });
        }
    }

    validateLength() {
        return this.validateInputValue(this.length, 'Length');
    }
    validateBreadth() {
        return this.validateInputValue(this.breadth, 'Breadth');
    }
    validateHeight() {
        return this.validateInputValue(this.height, 'Height');
    }

    getTotalVolume() {
        if (!this.hasValidationErrors())
            return (
                NumberUtils.toNumber(this.length) *
                NumberUtils.toNumber(this.breadth) *
                NumberUtils.toNumber(this.height)
            );
    }

    convertToSourceUnit = (unitConverter): this => {
        const { length, breadth, height } = this;

        const preferredUnit = unitConverter.getPreferredUnit({
            preferredUnits: preferredUnitsForDistance,
            sourceUnit: sourceUnitForDistance
        });

        const convertedLength = length
            ? unitConverter.basicConvert({
                  value: length ? NumberUtils.toNumber(length).toFixed(2) : 0,
                  sourceUnit: preferredUnit,
                  targetUnit: sourceUnitForDistance
              })
            : null;

        const convertedBreadth = breadth
            ? unitConverter.basicConvert({
                  value: breadth ? NumberUtils.toNumber(breadth).toFixed(2) : 0,
                  sourceUnit: preferredUnit,
                  targetUnit: sourceUnitForDistance
              })
            : null;

        const convertedHeight = height
            ? unitConverter.basicConvert({
                  value: height ? NumberUtils.toNumber(height).toFixed(2) : 0,
                  sourceUnit: preferredUnit,
                  targetUnit: sourceUnitForDistance
              })
            : null;

        let convertedDimensions = this.setLength(convertedLength);
        convertedDimensions = convertedDimensions.setBreadth(convertedBreadth);
        convertedDimensions = convertedDimensions.setHeight(convertedHeight);
        return convertedDimensions;
    };

    convertToPreferredUnit = (unitConverter): this => {
        const { length, breadth, height } = this;
        let convertedDimensions;
        const convertedLength = length
            ? unitConverter.convertMeters({
                  value: length ? NumberUtils.toNumber(length) : 0,
                  preferredUnits: preferredUnitsForDistance
              }).value
            : null;

        const convertedBreadth = breadth
            ? unitConverter.convertMeters({
                  value: breadth ? NumberUtils.toNumber(breadth) : 0,
                  preferredUnits: preferredUnitsForDistance
              }).value
            : null;

        const convertedHeight = height
            ? unitConverter.convertMeters({
                  value: height ? NumberUtils.toNumber(height) : 0,
                  preferredUnits: preferredUnitsForDistance
              }).value
            : null;

        convertedDimensions = this.setLength(convertedLength);
        convertedDimensions = convertedDimensions.setBreadth(convertedBreadth);
        convertedDimensions = convertedDimensions.setHeight(convertedHeight);
        return convertedDimensions;
    };
}

export { InternalDimensions as Dimensions };
